import React, { useState, useEffect } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutline } from '@heroicons/react/24/outline';
import { getProductReviews } from '../data/reviews';
import { toast } from 'react-hot-toast';

// localStorage utility functions for user reviews
const getUserReviews = (productId) => {
  try {
    const userReviews = localStorage.getItem(`userReviews_${productId}`);
    return userReviews ? JSON.parse(userReviews) : [];
  } catch (error) {
    console.error('Error loading user reviews:', error);
    return [];
  }
};

const saveUserReview = (productId, review) => {
  try {
    const existingReviews = getUserReviews(productId);
    const newReview = {
      ...review,
      id: Date.now(), // Use timestamp as unique ID
      date: new Date().toISOString().split('T')[0],
      isUserReview: true // Flag to identify user reviews
    };
    const updatedReviews = [newReview, ...existingReviews];
    localStorage.setItem(`userReviews_${productId}`, JSON.stringify(updatedReviews));
    return newReview;
  } catch (error) {
    console.error('Error saving user review:', error);
    return null;
  }
};

const getAllReviews = (productId) => {
  const existingReviews = getProductReviews(productId);
  const userReviews = getUserReviews(productId);
  return [...userReviews, ...existingReviews];
};

const getCombinedAverageRating = (productId) => {
  const allReviews = getAllReviews(productId);
  if (allReviews.length === 0) return 0;
  
  const sum = allReviews.reduce((acc, review) => acc + review.rating, 0);
  return (sum / allReviews.length).toFixed(1);
};

const StarRating = ({ rating, setRating = null }) => {
  const stars = [1, 2, 3, 4, 5];
  
  return (
    <div className="flex items-center space-x-1">
      {stars.map((star) => (
        <button
          key={star}
          onClick={() => setRating && setRating(star)}
          className={`${setRating ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
        >
          {star <= rating ? (
            <StarIcon className="h-6 w-6 text-yellow-400" />
          ) : (
            <StarOutline className="h-6 w-6 text-yellow-400" />
          )}
        </button>
      ))}
    </div>
  );
};

const ReviewForm = ({ productId, onReviewAdded }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [userName, setUserName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      toast.error('Please enter a review comment');
      return;
    }

    if (!userName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    // Save to localStorage instead of the main reviews data
    const newReview = saveUserReview(productId, {
      userName,
      rating,
      comment
    });

    if (newReview) {
      toast.success('Review saved successfully! It will persist in your browser.');
      onReviewAdded(newReview);

      // Reset form
      setRating(0);
      setComment('');
      setUserName('');
    } else {
      toast.error('Failed to save review. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-secondary/50 p-6 rounded-xl">
      <h3 className="text-xl font-semibold mb-4">Write a Review</h3>
      
      <div className="space-y-2">
        <label className="block text-sm text-gray-800">Your Rating</label>
        <StarRating rating={rating} setRating={setRating} />
      </div>

      <div className="space-y-2">
        <label htmlFor="userName" className="block text-sm text-gray-800">Your Name</label>
        <input
          type="text"
          id="userName"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          className="w-full px-4 py-2 bg-secondary border border-primary/20 rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
          placeholder="Enter your name"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="comment" className="block text-sm text-gray-800">Your Review</label>
        <textarea
          id="comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="w-full px-4 py-2 bg-secondary border border-primary/20 rounded-xl focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-all"
          rows="4"
          placeholder="Share your thoughts about this product..."
        />
      </div>

      <button
        type="submit"
        className="w-full btn btn-primary"
      >
        Submit Review
      </button>
    </form>
  );
};

const Reviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);

  // Load reviews on component mount
  useEffect(() => {
    const allReviews = getAllReviews(productId);
    const combinedRating = getCombinedAverageRating(productId);
    setReviews(allReviews);
    setAverageRating(combinedRating);
  }, [productId]);

  const handleReviewAdded = (newReview) => {
    // Reload all reviews to get the updated list
    const allReviews = getAllReviews(productId);
    const combinedRating = getCombinedAverageRating(productId);
    setReviews(allReviews);
    setAverageRating(combinedRating);
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-2xl font-semibold">Customer Reviews</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <StarRating rating={Math.round(averageRating)} />
            <span className="text-lg font-medium">{averageRating}</span>
          </div>
          <span className="text-gray-800">({reviews.length} reviews)</span>
        </div>
      </div>

      <ReviewForm productId={productId} onReviewAdded={handleReviewAdded} />

      <div className="space-y-6">
        {reviews.map((review) => (
          <div key={review.id} className={`p-4 sm:p-6 rounded-xl space-y-4 ${review.isUserReview ? 'bg-primary/10 border border-primary/20' : 'bg-secondary/30'}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
              <div className="flex items-center gap-4">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${review.isUserReview ? 'bg-primary/20' : 'bg-primary/10'}`}>
                  <span className="text-primary font-medium">
                    {review.userName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium">{review.userName}</h4>
                    {review.isUserReview && (
                      <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded-full">
                        Your Review
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800">{review.date}</p>
                </div>
              </div>
              <StarRating rating={review.rating} />
            </div>
            <p className="text-black font-medium">{review.comment}</p>
          </div>
        ))}

        {reviews.length === 0 && (
          <div className="text-center py-8 text-gray-800">
            No reviews yet. Be the first to review this product!
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews; 